import { Topic } from './Topic';
import { logger } from '../../logger';
import type { Action, Feature, FeatureStatus } from '@otto-ec/tracking-bct';

export type TrackingFeature = {
	readonly id: string,
	readonly name: string,
	readonly status: string,
	readonly parentId?: string,
	readonly labels?: Record<string, string[]>
	position?: number
};

export enum ActionName {
	CLOSE = 'close',
	CLICK = 'click',
	SCROLL = 'scroll',
	OPEN = 'open',
	SUBMIT = 'submit',
	NEXT = 'next',
	SELECT = 'select'
}

export default class TrackingService {
	private trackingMap: Map<Topic, (a: Record<string, string[]>) => void>;

	constructor() {
		this.trackingMap = new Map<Topic, (trackingAction: Record<string, string[]>) => void>([
			[Topic.SubmitEvent, this.submitEvent],
			[Topic.MergeEvent, this.submitMerge],
			[Topic.MoveEvent, this.moveEvent],
			[Topic.AddFeaturesToPageImpression, this.submitMerge],
			[Topic.AddActionToEvent, this.submitEvent]
		]);
	}

	async submitEvent(event: Record<string, string[]>, action?: Action) {
		const { submitEvent } = await import ('@otto-ec/tracking-bct');
		if (action) {
			submitEvent({}, action);
		}
		submitEvent(event);
	}

	async submitMerge(event: Record<string, string[]>, features?: Feature[]) {
		const { submitMerge } = await import ('@otto-ec/tracking-bct');
		if (features) {
			submitMerge({}, features);
		}
		submitMerge(event);
	}

	async submitTracking(topic: Topic, features: Feature[], actionName: ActionName | null) {
		if (Topic.AddFeaturesToPageImpression == topic) {
			await this.submitMerge({}, features);
			logger.tracking(topic, JSON.stringify(features));
		} else if (Topic.AddActionToEvent == topic) {
			const trackingPayload = {
				name: actionName as ActionName,
				features: features
			} satisfies Action;
			await this.submitEvent({}, trackingPayload);
			logger.tracking(topic, JSON.stringify(trackingPayload));
		}
	}

	private async moveEvent(event: Record<string, string[]>) {
		const { submitMove } = await import ('@otto-ec/tracking-bct');
		submitMove(event);
	}

	async send(topic: Topic, trackingPayload: Record<string, string>) {
		if (!this.trackingMap.has(topic)) {
			logger.error(`Topic: ${topic} is not supported when sending tracking via Nexus`);
			return;
		}

		const trackingBCTRecord: Record<string, string[]> = this.toLinkedMap(trackingPayload);

		this.trackingMap.get(topic)!(trackingBCTRecord);
		logger.tracking(topic, trackingPayload);
	}

	async sendFeatureTracking(
		topic: Topic,
		id: string,
		name: string,
		featureName: string,
		status: string,
		labelsForParent: Record<string, string>,
		labelsForChildren: Record<string, string>,
		actionName: ActionName | null = null) {
		const features: Feature[] = [
			{
				id: id,
				name: name,
				status: status as FeatureStatus,
				labels: this.toLinkedMap(labelsForParent)
			},
			{
				id: `${id}_tile`,
				name: featureName,
				parentId: id,
				status: status as FeatureStatus,
				labels: this.toLinkedMap(labelsForChildren)
			}
		];

		if (Topic.AddFeaturesToPageImpression == topic) {
			await this.submitMerge({}, features);

			//@ts-ignore
			logger.tracking(topic, JSON.stringify(features));
		} else if (Topic.AddActionToEvent == topic) {

			const action = {
				name: actionName!,
				features: features
			} satisfies Action;

			await this.submitEvent({}, action);
			//@ts-ignore
			logger.tracking(topic, JSON.stringify(action));
		}
	}

	async submitMiniAction(event: { featureId: string }[]) {
		const { submitMiniAction } = await import ('@otto-ec/tracking-bct');
		submitMiniAction(event);
	}

	private toLinkedMap(map: Record<string, string> | undefined): Record<string, string[]> {
		if (!map) {
			return {};
		}
		const result: Record<string, string[]> = {};
		Object.keys(map).map((key) => {
			result[key] = [map[key]];
		});

		return result;
	}
}
